import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from './common/SafeIcon';
import Navbar from './components/Navbar';
import WeatherSection from './components/WeatherSection';
import SitePartner from './components/SitePartner';
import ShareSection from './components/ShareSection';
import ArticlePage from './pages/ArticlePage';
import KingsCounty from './pages/KingsCounty';
import QueensCounty from './pages/QueensCounty';
import PrinceCounty from './pages/PrinceCounty';
import SchoolsPage from './pages/SchoolsPage';
import DrivingPage from './pages/DrivingPage';
import BusesPage from './pages/BusesPage';
import WeatherGurusPage from './pages/WeatherGurusPage';

const Dashboard = () => {
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(true);

  const LAT = 46.243;
  const LON = -63.127;

  useEffect(() => {
    const fetchWeather = async () => {
      try {
        const response = await fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${LAT}&longitude=${LON}&daily=weathercode,temperature_2m_max,temperature_2m_min,precipitation_probability_max,windspeed_10m_max&timezone=America%2FHalifax`
        );
        const data = await response.json();
        setForecast({
          tomorrow: {
            date: data.daily.time[1],
            weatherCode: data.daily.weathercode[1],
            maxTemp: data.daily.temperature_2m_max[1],
            minTemp: data.daily.temperature_2m_min[1],
            precipProb: data.daily.precipitation_probability_max[1],
            windspeed: data.daily.windspeed_10m_max[1],
          },
        });
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchWeather();
  }, []);

  if (loading) return null;

  const countyLinks = [
    { name: 'Queens County', path: '/queens', img: 'https://res.cloudinary.com/dufyzcc72/image/upload/v1773310180/queen_ebmdjm.jpg' },
    { name: 'Kings County', path: '/kings', img: 'https://res.cloudinary.com/dufyzcc72/image/upload/v1773310180/king_rkqdmr.jpg' },
    { name: 'Prince County', path: '/prince', img: 'https://res.cloudinary.com/dufyzcc72/image/upload/v1773310180/prince_zjtnqt.jpg' }
  ];

  return (
    <div className="min-h-screen bg-slate-100 py-12 px-4 font-sans text-slate-900">
      <div className="max-w-2xl mx-auto flex flex-col gap-8">
        <div className="text-center">
          <h1 className="text-4xl font-black text-slate-900 tracking-tight uppercase">PEI Travel Dashboard</h1>
          <p className="mt-2 text-slate-600 font-bold uppercase tracking-widest flex items-center justify-center gap-2">
            <SafeIcon name="MapPin" className="text-blue-500" /> All your PEI driving and weather links in one place
          </p>
        </div>

        <SitePartner />

        <WeatherSection 
          title="1. Today's Weather Charlottetown" 
          delay={0.1} 
          headerClassName="bg-purple-600 text-white"
          link="https://weather.gc.ca/en/location/index.html?coords=46.243,-63.127"
          buttonText="Link To Charlottetown Weather"
          imageUrl="https://res.cloudinary.com/dufyzcc72/image/upload/v1773272098/Charlottetown_egpkrs.png"
        />

        <div className="grid grid-cols-1 gap-6">
          {countyLinks.map((county, index) => (
            <Link key={county.path} to={county.path} className="group block relative aspect-video overflow-hidden rounded-3xl shadow-lg border border-white/20">
              <img src={county.img} alt={county.name} className="w-full h-full object-contain bg-slate-900 transition-transform duration-500 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent flex items-end p-8">
                <h2 className="text-white text-3xl font-black uppercase tracking-tighter">{county.name}</h2>
              </div>
            </Link>
          ))}
        </div>

        <WeatherSection 
          title="2. Tomorrow's Forecast" 
          date={forecast.tomorrow.date} 
          data={forecast.tomorrow} 
          delay={0.3} 
          headerClassName="bg-green-600 text-white"
        />

        <SitePartner className="mt-4" />

        {/* SOCIAL SHARING ICONS */}
        <ShareSection />
      </div>
    </div>
  );
};

function App() {
  return (
    <Router>
      <div className="flex flex-col min-h-screen">
        <Navbar />
        <main className="flex-grow">
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/weather-gurus" element={<WeatherGurusPage />} />
            <Route path="/kings" element={<KingsCounty />} />
            <Route path="/queens" element={<QueensCounty />} />
            <Route path="/prince" element={<PrinceCounty />} />
            <Route path="/schools" element={<SchoolsPage />} />
            <Route path="/driving" element={<DrivingPage />} />
            <Route path="/buses" element={<BusesPage />} />
            <Route path="/safety/:id" element={<ArticlePage />} />
          </Routes>
        </main>
      </div>
    </Router>
  );
}

export default App;